import React from 'react';
import { WifiOff, RefreshCw, AlertCircle, Wifi } from 'lucide-react';

const NoInternet = ({ onRetry, isRetrying = false, showReconnected = false }) => {
  if (showReconnected) {
    return (
      <div style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        zIndex: 9999,
        backgroundColor: '#10b981',
        color: 'white',
        padding: '12px 20px',
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        animation: 'slideInRight 0.3s ease-out'
      }}>
        <Wifi size={20} />
        <span style={{ fontWeight: '500' }}>Connection restored!</span>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: '#f9fafb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        textAlign: 'center',
        maxWidth: '400px',
        width: '100%'
      }}>
        {/* Animated WiFi Off Icon */}
        <div style={{
          marginBottom: '24px',
          position: 'relative',
          display: 'inline-block'
        }}>
          <div style={{
            width: '120px',
            height: '120px',
            backgroundColor: '#fee2e2',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto',
            animation: 'pulse 2s infinite'
          }}>
            <WifiOff size={60} color="#dc2626" />
          </div>
          
          {/* Animated ripple effect */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '120px',
            height: '120px',
            border: '2px solid #fca5a5',
            borderRadius: '50%',
            animation: 'ripple 2s infinite'
          }} />
        </div>

        {/* Title */}
        <h1 style={{
          fontSize: '28px',
          fontWeight: 'bold',
          color: '#111827',
          marginBottom: '12px'
        }}>
          No Internet Connection
        </h1>

        {/* Description */}
        <p style={{
          fontSize: '16px',
          color: '#6b7280',
          marginBottom: '32px',
          lineHeight: '1.5'
        }}>
          Please check your internet connection and try again. Make sure you're connected to WiFi or mobile data.
        </p>

        {/* Status indicators */}
        <div style={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '32px'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            <AlertCircle size={20} color="#f59e0b" />
            Connection Status
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0'
            }}>
              <span style={{ color: '#6b7280', fontSize: '14px' }}>Network Status:</span>
              <span style={{
                color: '#dc2626',
                fontSize: '14px',
                fontWeight: '500',
                backgroundColor: '#fee2e2',
                padding: '2px 8px',
                borderRadius: '12px'
              }}>
                Offline
              </span>
            </div>
            
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 0'
            }}>
              <span style={{ color: '#6b7280', fontSize: '14px' }}>Last Updated:</span>
              <span style={{ color: '#6b7280', fontSize: '14px' }}>
                {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>
        </div>

        {/* Retry Button */}
        <button
          onClick={onRetry}
          disabled={isRetrying}
          style={{
            backgroundColor: isRetrying ? '#9ca3af' : '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            padding: '12px 24px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isRetrying ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            width: '100%',
            maxWidth: '200px',
            margin: '0 auto',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            if (!isRetrying) {
              e.target.style.backgroundColor = '#2563eb';
              e.target.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isRetrying) {
              e.target.style.backgroundColor = '#3b82f6';
              e.target.style.transform = 'translateY(0)';
            }
          }}
        >
          <RefreshCw 
            size={20} 
            style={{
              animation: isRetrying ? 'spin 1s linear infinite' : 'none'
            }}
          />
          {isRetrying ? 'Checking...' : 'Try Again'}
        </button>

        {/* Helpful tips */}
        <div style={{
          marginTop: '40px',
          padding: '16px',
          backgroundColor: '#eff6ff',
          borderRadius: '8px',
          border: '1px solid #dbeafe'
        }}>
          <h4 style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1e40af',
            marginBottom: '8px'
          }}>
            Troubleshooting Tips:
          </h4>
          <ul style={{
            fontSize: '13px',
            color: '#3730a3',
            textAlign: 'left',
            margin: 0,
            paddingLeft: '16px'
          }}>
            <li>Check your WiFi or mobile data connection</li>
            <li>Try turning your WiFi off and on again</li>
            <li>Move closer to your router</li>
            <li>Contact your internet service provider</li>
          </ul>
        </div>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes ripple {
          0% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          100% { transform: translate(-50%, -50%) scale(1.5); opacity: 0; }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default NoInternet;
